import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, Wand2, Truck, Ban, Route, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Panneau d'affectation : s'ouvre au clic sur « Affecter » (une course) ou depuis la
 * barre de sélection multiple. Glisse depuis le bas — utilisable au doigt sur tablette.
 */
export function AssignSheet({ open, rideIds, refRideId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const bulk = rideIds.length > 1;

  const load = useCallback(() => {
    if (!refRideId) return;
    api.get(`/rides/${refRideId}/suggestions`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(apiError(err)));
  }, [refRideId]);

  useEffect(() => { if (open) { setData(null); load(); } }, [open, load]);

  const assign = async (vehicleId) => {
    setBusy(vehicleId || "none");
    try {
      if (bulk) {
        const { data: res } = await api.post("/rides/bulk-assign", { ride_ids: rideIds, vehicle_id: vehicleId });
        (res.warnings || []).slice(0, 2).forEach((w) => toast.warning(w));
        if (res.failed?.length) toast.error(`${res.failed.length} course(s) non affectée(s)`);
        toast.success(`${res.assigned} course(s) ${vehicleId ? "affectées" : "désaffectées"}`);
      } else {
        const { data: res } = await api.put(`/rides/${rideIds[0]}/assign`, { vehicle_id: vehicleId });
        if (res.warning) toast.warning(res.warning);
        else toast.success(vehicleId ? "Course affectée" : "Course désaffectée");
      }
      onDone();
      onClose();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  const autoAssign = async () => {
    setBusy("auto");
    try {
      for (const rid of rideIds) {
        const { data: res } = await api.post(`/rides/${rid}/auto-assign`);
        if (res.warning) toast.warning(res.warning);
      }
      toast.success(rideIds.length > 1 ? `${rideIds.length} courses affectées au mieux` : "Course affectée au mieux");
      onDone();
      onClose();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  if (!open) return null;

  return (
    <>
      <div className="no-print fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]" onClick={onClose} data-testid="assign-sheet-overlay" />
      <div
        className="no-print fixed z-50 left-0 right-0 bottom-0 mx-auto w-full sm:max-w-2xl bg-card border border-border rounded-t-2xl shadow-2xl flex flex-col max-h-[78vh] animate-in slide-in-from-bottom duration-200"
        data-testid="assign-sheet"
      >
        <header className="px-4 py-3 border-b border-border flex items-start gap-3 shrink-0">
          <div className="min-w-0">
            <p className="font-display font-bold text-base">
              {bulk ? `Affecter ${rideIds.length} courses` : "Affecter la course"}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {data ? `${data.time} · ${data.patient_name}${bulk ? " et autres" : ""} · catégorie ${data.vehicle_type || "—"}` : "Analyse des véhicules…"}
            </p>
          </div>
          <Button size="sm" onClick={autoAssign} disabled={!!busy} className="ml-auto gap-2 shrink-0" data-testid="assign-best">
            {busy === "auto" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Affecter au mieux
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0" data-testid="assign-close">
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="overflow-y-auto p-2 space-y-1.5" data-testid="assign-list">
          {!data && <p className="text-sm text-muted-foreground text-center py-8">Chargement des propositions…</p>}
          {data?.suggestions.map((s, i) => (
            <button
              key={s.vehicle_id}
              type="button"
              disabled={!s.compatible || !!busy}
              onClick={() => assign(s.vehicle_id)}
              className={`w-full text-left rounded-lg border p-2.5 transition-colors flex items-start gap-3
                ${s.compatible ? "border-border hover:border-primary hover:bg-primary/5" : "border-border/60 opacity-55 cursor-not-allowed"}
                ${s.is_current ? "bg-secondary" : ""}`}
              data-testid={`assign-option-${s.vehicle_id}`}
            >
              <div className={`mt-0.5 h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${s.compatible ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                {s.compatible ? <Truck className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{s.label}</span>
                  {i === 0 && s.compatible && !s.is_current && <Badge className="text-[10px] bg-primary text-primary-foreground hover:bg-primary">Meilleur choix</Badge>}
                  {s.is_current && <Badge variant="outline" className="text-[10px]">Véhicule actuel</Badge>}
                  <span className="text-xs text-muted-foreground">{s.rides_count} course(s)</span>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {s.depot} · {s.crew_names.length ? s.crew_names.join(", ") : "aucun chauffeur affecté ce jour"}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                  {s.empty_km !== null && (
                    <span className="inline-flex items-center gap-1 text-foreground/80">
                      <Route className="h-3 w-3" /> {s.empty_km} km à vide <span className="opacity-60">({s.empty_from})</span>
                    </span>
                  )}
                  {s.available_from && (
                    <span className="inline-flex items-center gap-1 text-foreground/80">
                      <Clock className="h-3 w-3" /> libre à {s.available_from}
                    </span>
                  )}
                  {s.compatible && !s.warning && (
                    <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                      <CheckCircle2 className="h-3 w-3" /> compatible
                    </span>
                  )}
                  {s.warning && <span className="text-amber-600 dark:text-amber-400 font-medium">⚠ {s.warning}</span>}
                  {s.blocking && <span className="text-destructive font-medium">⛔ {s.blocking}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>

        <footer className="px-3 py-2.5 border-t border-border shrink-0 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => assign(null)} disabled={!!busy} data-testid="assign-unassign">
            Désaffecter
          </Button>
          <span className="text-[11px] text-muted-foreground ml-auto">
            Les véhicules grisés indiquent le motif du blocage. Le glisser-déposer reste disponible.
          </span>
        </footer>
      </div>
    </>
  );
}
